import { UnitedDashboardHeader } from './UnitedDashboardHeader';
import { PanelsProvider } from '../../../panels/PanelsContext';
import { usePageHelpers } from './usePageHelpers';
import { Navigate } from 'react-router';
import { appRoutes } from '../../../../constants/appRoutes';
import { ErrorPage } from '../../../common/ErrorPage';
import { ConfigurationCanvasProvider } from '../../configuration-canvas/ConfigurationCanvasContext';
import { useSelector } from 'react-redux';
import {
  selectFunnelMap,
  selectFunnelMapIsLoaded,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { useUnitedConnectedFunnelMapCanvas } from './useUnitedConnectedFunnelMapCanvas';
import { noop } from 'lodash';
import { CanvasBoard } from '../../configuration-canvas/canvas-container/CanvasBoard';
import { UnitedConnectedFunnelMapFlowCanvas } from './UnitedConnectedFunnelMapFlowCanvas';
import { CanvasUnitedConfigurationPage } from './canvas-page/CanvasUnitedConfigurationPage';
import { Box } from '@mui/material';
import { PlacingPluginContextProvider } from '../../funnel-map-flow/templates/placing-plugin/PlacingPluginContext';
import useConfirm from '../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { useInitializeFunnelMapPage, useLoadEnv } from './useInitializeFunnelMapPage';
import { Loader } from '../../../common/Loader';
import { UndoRedo } from '../../funnel-map-flow/UndoRedo';
import { CreateFieldContextProvider } from '../../funnel-map-flow/dialogs/deprecated-fields-management/create-field-dialog/CreateFieldContext';
import { useInitializeNonParsedObjectsInCanvas } from '../../../documentation/hooks/useInitializeNonParsedObjectsInCanvas';
import { FunnelMapCanvasCtxProvider } from '../../../funnel-map-canvas/FunnelMapCanvasCtx';
import useQueryParams from '../../../../navigation-utils/useQueryParams';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import useUserInfo from '../../../../hooks/useUserInfo';
import { useEffect } from 'react';
import { SweepCanvasProvider } from '../../../sweep-canvas/SweepCanvasProvider';
import { DASHBOARD_HEADER_HEIGHT } from '../../../../constants';
import { usePreemptivelyLoadFunnelMapOrgs } from './usePreemptivelyLoadFunnelMapOrgs';
import { SfFunnelMenuContextProvider } from '../../funnel-map-flow/dialogs/sf-funnel-menu/SfFunnelMenuProvider';
import { MetadataMoreCtxProvider } from '../../funnel-map-flow/dialogs/metadata-elements/more-menu/MetatadataMoreCtx';
import { AgentChatInstanceProvider } from '../../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../../aiAgentCenter/ChatInstance';
import { AutoSaveProvider } from '../../funnel-map-flow/auto-save/AutoSaveContext';

const UnitedConfigurationCanvasContextConsumer = ({
  funnelMap,
  crmOrgId,
}: {
  funnelMap: FunnelMap;
  funnelMapId?: string;
  crmOrgId: string;
}) => {
  const dashboardHeight = DASHBOARD_HEADER_HEIGHT;
  const { onSweepElementsChange, canvasNodesMouseMove } = useUnitedConnectedFunnelMapCanvas({
    funnelMap,
    readonly: false,
    crmOrgId,
  });
  useLoadEnv();
  useInitializeNonParsedObjectsInCanvas(funnelMap);
  usePreemptivelyLoadFunnelMapOrgs(funnelMap);

  return (
    <ConfigurationCanvasProvider
      canvasCrmOrgId={crmOrgId}
      canvasFunnelMap={funnelMap}
      setCanvasFunnelMap={noop}
      onSweepElementsChange={onSweepElementsChange}
    >
      <MetadataMoreCtxProvider>
        <SweepCanvasProvider>
          <CreateFieldContextProvider funnelMapId={funnelMap.id}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '100vh',
              }}
            >
              <UndoRedo />
              <Box
                sx={{
                  position: 'absolute',
                  top: dashboardHeight,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                <CanvasBoard
                  page={<CanvasUnitedConfigurationPage />}
                  crmOrgId={crmOrgId}
                  funnelMap={funnelMap}
                  hideCommentsButton
                >
                  <SfFunnelMenuContextProvider>
                    <UnitedConnectedFunnelMapFlowCanvas
                      canvasNodesMouseMove={canvasNodesMouseMove}
                      onSweepElementsChange={onSweepElementsChange}
                    />
                  </SfFunnelMenuContextProvider>
                </CanvasBoard>
              </Box>
              <Box sx={{ position: 'absolute', top: 0, right: 0, left: 0 }}>
                <UnitedDashboardHeader />
              </Box>
            </Box>
          </CreateFieldContextProvider>
        </SweepCanvasProvider>
      </MetadataMoreCtxProvider>
    </ConfigurationCanvasProvider>
  );
};

const useHandleCrmOrgInDeepLink = () => {
  const query = useQueryParams();
  const { updateDefaultCreationCrmOrgId } = useUserInfo();
  const currentCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const crmOrgInParams = query.get(DeepLinkSearchParams.crmOrgId);

  useEffect(() => {
    if (crmOrgInParams && crmOrgInParams !== currentCrmOrgId) {
      updateDefaultCreationCrmOrgId(crmOrgInParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return crmOrgInParams ?? currentCrmOrgId;
};

const UnitedConfigurationCanvas = () => {
  const funnelMap = useSelector(selectFunnelMap);
  const isFunnelMapLoaded = useSelector(selectFunnelMapIsLoaded);
  const { funnelMapId, hasFetchingErrors, isLoading } = usePageHelpers();

  const crmOrgId = useHandleCrmOrgInDeepLink();
  useInitializeFunnelMapPage();

  if (!funnelMapId) {
    return <Navigate to={appRoutes.funnelMapsPage.route} />;
  }

  if (hasFetchingErrors) {
    return <ErrorPage />;
  }

  return (
    <div data-testid="funnel-flow-page">
      {isLoading || !crmOrgId || !isFunnelMapLoaded ? (
        <Loader />
      ) : (
        <div data-testid="funnel-flow-page-loaded-canvas">
          <FunnelMapCanvasCtxProvider>
            <UnitedConfigurationCanvasContextConsumer
              funnelMap={funnelMap}
              funnelMapId={funnelMapId}
              crmOrgId={crmOrgId}
            />
          </FunnelMapCanvasCtxProvider>
        </div>
      )}
    </div>
  );
};

const UnitedDashboardWithPanels = () => {
  const { openConfirm } = useConfirm();

  return (
    <AutoSaveProvider>
      <AgentChatInstanceProvider instanceId={ChatInstance.agentsPanel}>
        <PlacingPluginContextProvider>
          <PanelsProvider
            confirmOpen={async ({ action }) => {
              const confirmLeave = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
              if (confirmLeave) {
                action();
              }
            }}
          >
            <UnitedConfigurationCanvas />
          </PanelsProvider>
        </PlacingPluginContextProvider>
      </AgentChatInstanceProvider>
    </AutoSaveProvider>
  );
};

export { UnitedDashboardWithPanels as UnitedDashboard };
